// src/services/auth.service.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions, googleProvider } from "@/firebase";
import { Logger } from "@/utils/logger";
import { FeatureFlags } from "@/config/features";
import { ONBOARDING_STATUS } from "./database.schema";
import { shallowReactive } from "vue";

// Current user state
const currentUser = shallowReactive({
  user: null,
  loading: true,
  error: null,
});

// ── Google sign-in role-selection handoff ────────────────────────────────────
// A first-time Google sign-in has no Firestore user doc and no role choice
// yet (unlike email/password registration, which captures the role as part
// of the signup form). This sessionStorage flag tells initializeAuthListener
// to skip its usual "create a default student doc" fallback so the
// /choose-role interstitial can create the doc once the user picks a role.
const GOOGLE_PENDING_ROLE_SELECTION_KEY =
  "crescendo_google_pending_role_selection";

const hasPendingGoogleRoleSelection = () => {
  try {
    return sessionStorage.getItem(GOOGLE_PENDING_ROLE_SELECTION_KEY) === "true";
  } catch {
    return false;
  }
};

// Set while registerUser is between "Auth account created" and "Firestore docs
// written". createUserWithEmailAndPassword fires onAuthStateChanged straight
// away, so without this the listener below reads users/{uid} before
// registerUser has written it, takes its "no Firestore doc" branch, and writes
// a default student doc — which lands *after* registerUser's teacher doc and
// silently demotes the new teacher to a student. Same purpose as the Google
// role-selection flag above, for the email/password path.
let registrationInProgress = false;

const setPendingGoogleRoleSelection = (value) => {
  try {
    if (value) {
      sessionStorage.setItem(GOOGLE_PENDING_ROLE_SELECTION_KEY, "true");
    } else {
      sessionStorage.removeItem(GOOGLE_PENDING_ROLE_SELECTION_KEY);
    }
  } catch {
    // sessionStorage unavailable (e.g. private browsing) - best effort only
  }
};

// ── Crisp support chat ───────────────────────────────────────────────────────
// Website ID is public (it appears in page source on every Crisp-enabled
// site). Overridable per environment; set the env var to "" to ship a build
// without support chat.
const CRISP_WEBSITE_ID =
  process.env.VUE_APP_CRISP_WEBSITE_ID !== undefined
    ? process.env.VUE_APP_CRISP_WEBSITE_ID
    : "11752b33-bb1b-4d5e-bc04-7c4fdd0590c4";

let crispLoadPromise = null;

// Lazy-load the Crisp SDK only in browser environments with a configured id.
const loadCrisp = () => {
  if (crispLoadPromise) return crispLoadPromise;
  if (typeof window === "undefined" || !CRISP_WEBSITE_ID) {
    return Promise.resolve(null);
  }

  crispLoadPromise = import("crisp-sdk-web")
    .then((module) => {
      const Crisp = module.Crisp;
      Crisp.configure(CRISP_WEBSITE_ID);
      return Crisp;
    })
    .catch((error) => {
      Logger.log(
        "warn",
        "Failed to load Crisp SDK",
        { error: error.message },
        "AuthService"
      );
      return null;
    });

  return crispLoadPromise;
};

// Identify the signed-in user to Crisp so support conversations carry their
// name/email and role.
const initializeSupportChat = async (user) => {
  try {
    const Crisp = await loadCrisp();
    if (!Crisp) return;

    if (user.email) {
      Crisp.user.setEmail(user.email);
    }
    const displayName = user.name || user.displayName;
    if (displayName) {
      Crisp.user.setNickname(displayName);
    }
    Crisp.session.setData({
      user_id: user.uid,
      user_type: user.userType || "student",
      is_teacher: Boolean(user.isTeacher),
    });

    Logger.log(
      "info",
      "Crisp support chat initialized",
      { userId: user.uid },
      "AuthService"
    );
  } catch (error) {
    Logger.log(
      "error",
      "Failed to initialize Crisp",
      { error: error.message },
      "AuthService"
    );
  }
};

// Reset the Crisp session on logout so the next user on this browser doesn't
// see the previous user's conversation history.
const shutdownSupportChat = async () => {
  try {
    if (!crispLoadPromise) return; // chat never booted
    const Crisp = await crispLoadPromise;
    if (Crisp) {
      Crisp.session.reset();
      Logger.log("info", "Crisp session reset", null, "AuthService");
    }
  } catch (error) {
    Logger.log(
      "error",
      "Failed to reset Crisp session",
      { error: error.message },
      "AuthService"
    );
  }
};

// Checks for a pending Google signInWithRedirect result (the fallback path
// used when signInWithPopup is blocked) and marks the role-selection flag if
// it resolved to a new user, before the auth-state listener subscribes.
const checkGoogleRedirectResult = async () => {
  try {
    const redirectResult = await getRedirectResult(auth);
    if (redirectResult && getAdditionalUserInfo(redirectResult)?.isNewUser) {
      setPendingGoogleRoleSelection(true);
    }
  } catch (error) {
    Logger.log(
      "warn",
      "Error checking Google redirect result",
      { error: error.message },
      "AuthService"
    );
  }
};

// Initialize the auth listener
const initializeAuthListener = () => {
  Logger.log("info", "Initializing auth listener", null, "AuthService");
  currentUser.loading = true;

  // Flag to skip the second onAuthStateChanged fired by updateProfile()
  let profileUpdateInProgress = false;

  return new Promise((resolve) => {
    checkGoogleRedirectResult().then(() => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (profileUpdateInProgress) return;
        Logger.log(
          "info",
          "Auth state changed",
          { userId: user?.uid, email: user?.email },
          "AuthService"
        );

        if (user) {
          try {
            // Get the user document from Firestore
            const userDocRef = doc(db, "users", user.uid);
            const userDoc = await getDoc(userDocRef);

            Logger.log(
              "debug",
              "Fetching user document from Firestore",
              { userId: user.uid },
              "AuthService"
            );

            if (userDoc.exists()) {
              // Ensure we have all the correct properties
              const userData = userDoc.data();
              Logger.log(
                "debug",
                "Retrieved user data from Firestore",
                { userType: userData.userType, isTeacher: userData.isTeacher },
                "AuthService"
              );

              // Create a complete user object with Auth and Firestore data
              currentUser.user = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName || userData.name,
                photoURL: user.photoURL,
                name: userData.name || user.displayName,
                userType: userData.userType || "student",
                isTeacher:
                  userData.isTeacher === true ||
                  userData.userType === "teacher",
                onboardingStatus:
                  userData.onboardingStatus || ONBOARDING_STATUS.PENDING,
                onboardingCompletedAt: userData.onboardingCompletedAt,
                createdAt: userData.createdAt,
              };

              Logger.log(
                "info",
                "Updated current user object",
                {
                  userType: currentUser.user.userType,
                  isTeacher: currentUser.user.isTeacher,
                },
                "AuthService"
              );

              // Identify the user to the support chat
              initializeSupportChat(currentUser.user);

              // If we need to update display name in Auth
              if (!user.displayName && userData.name) {
                try {
                  profileUpdateInProgress = true;
                  await updateProfile(user, { displayName: userData.name });
                  Logger.log(
                    "info",
                    "Updated user display name in Firebase Auth",
                    { displayName: userData.name },
                    "AuthService"
                  );
                } catch (e) {
                  Logger.log(
                    "error",
                    "Could not update display name in Firebase Auth",
                    { error: e.message },
                    "AuthService"
                  );
                } finally {
                  profileUpdateInProgress = false;
                }
              }
            } else if (hasPendingGoogleRoleSelection()) {
              // First-time Google sign-in awaiting the role-selection
              // interstitial (see completeGoogleSignup) - leave currentUser.user
              // null rather than defaulting to student, so the app can route
              // the user to /choose-role instead of creating a doc for them.
              Logger.log(
                "info",
                "Skipping fallback user doc creation - Google role selection pending",
                { userId: user.uid },
                "AuthService"
              );
              currentUser.user = null;
            } else if (registrationInProgress) {
              // registerUser has created the Auth account but hasn't written
              // its Firestore docs yet. Creating a default student doc here
              // would race — and win — against the role the user actually
              // chose. registerUser populates currentUser.user itself.
              Logger.log(
                "info",
                "Skipping fallback user doc creation - registration in progress",
                { userId: user.uid },
                "AuthService"
              );
            } else {
              // User exists in Auth but not in Firestore - create a basic record
              currentUser.user = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                isTeacher: false,
                userType: "student",
                onboardingStatus: ONBOARDING_STATUS.PENDING,
                onboardingCompletedAt: null,
              };

              Logger.log(
                "info",
                "Created basic user object for authenticated user without Firestore document",
                { userId: user.uid },
                "AuthService"
              );

              // Identify the user to the support chat (basic data)
              initializeSupportChat(currentUser.user);

              // Create a basic user document if none exists
              await setDoc(userDocRef, {
                name: user.displayName || "",
                email: user.email,
                userType: "student",
                isTeacher: false,
                onboardingStatus: ONBOARDING_STATUS.PENDING,
                onboardingCompletedAt: null,
                createdAt: new Date(),
              });
            }
          } catch (error) {
            Logger.log(
              "error",
              "Error getting user data from Firestore",
              { error: error.message },
              "AuthService"
            );
            currentUser.user = null;
            currentUser.error = "Error loading user data";
          }
        } else {
          Logger.log(
            "info",
            "No user logged in, clearing current user state",
            null,
            "AuthService"
          );

          // Reset support chat session when user logs out
          shutdownSupportChat();

          currentUser.user = null;
        }

        currentUser.loading = false;
        Logger.log(
          "info",
          "Auth listener initialization complete",
          { hasUser: !!currentUser.user },
          "AuthService"
        );
        resolve(currentUser);
      });

      return unsubscribe;
    });
  });
};

// Shared users/{uid} doc shape for both email/password registration and
// Google signup, so the two creation paths can't drift apart.
const buildUserDocument = ({ name, email, userType }) => {
  const isTeacher = userType === "teacher";
  return {
    isTeacher,
    doc: {
      name,
      email,
      userType,
      isTeacher,
      onboardingStatus: isTeacher
        ? ONBOARDING_STATUS.COMPLETED
        : ONBOARDING_STATUS.PENDING,
      onboardingCompletedAt: isTeacher ? new Date() : null,
      createdAt: new Date(),
    },
  };
};

// Shared teachers/{uid} profile doc shape.
const buildTeacherProfileDocument = ({ name, email }) => ({
  name,
  email,
  instrument: "",
  bio: "",
  hourlyRate: 0,
  rating: 0,
  reviewCount: 0,
  tags: [],
  availability: [],
  createdAt: new Date(),
});

// Register a new user
const registerUser = async (email, password, name, userType = "student") => {
  Logger.log(
    "info",
    "Starting user registration",
    { email, userType },
    "AuthService"
  );

  // Must be set before the Auth account exists: createUserWithEmailAndPassword
  // fires onAuthStateChanged during the call, not after it.
  registrationInProgress = true;

  try {
    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    Logger.log(
      "info",
      "Created user in Firebase Auth",
      { userId: user.uid },
      "AuthService"
    );

    // Update display name in Auth
    await updateProfile(user, { displayName: name });

    // Create user document in Firestore
    const { doc: userDocData, isTeacher } = buildUserDocument({
      name,
      email,
      userType,
    });
    await setDoc(doc(db, "users", user.uid), userDocData);

    // If user is a teacher, create teacher profile
    if (isTeacher) {
      await setDoc(
        doc(db, "teachers", user.uid),
        buildTeacherProfileDocument({ name, email })
      );

      Logger.log(
        "info",
        "Created teacher profile document",
        { userId: user.uid },
        "AuthService"
      );
    }

    Logger.log(
      "info",
      "User registration completed successfully",
      { userId: user.uid, userType },
      "AuthService"
    );

    currentUser.user = {
      uid: user.uid,
      email: user.email,
      displayName: name,
      photoURL: user.photoURL ?? null,
      ...userDocData,
    };

    return user;
  } catch (error) {
    Logger.log(
      "error",
      "Error during user registration",
      { error: error.message, email, userType },
      "AuthService"
    );
    throw error;
  } finally {
    registrationInProgress = false;
  }
};

// Completes signup for a first-time Google sign-in once the user has picked
// a role on the /choose-role interstitial. The Firebase Auth account already
// exists (created by signInWithGoogle's signInWithPopup); this only creates
// the Firestore doc(s) that a traditional registerUser call would have.
const completeGoogleSignup = async (userType) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("No authenticated Google user to complete signup for");
  }

  Logger.log(
    "info",
    "Completing Google signup",
    { userId: user.uid, userType },
    "AuthService"
  );

  try {
    const name = user.displayName || "";
    const email = user.email;

    const { doc: userDocData, isTeacher } = buildUserDocument({
      name,
      email,
      userType,
    });
    await setDoc(doc(db, "users", user.uid), userDocData);

    if (isTeacher) {
      await setDoc(
        doc(db, "teachers", user.uid),
        buildTeacherProfileDocument({ name, email })
      );

      Logger.log(
        "info",
        "Created teacher profile document",
        { userId: user.uid },
        "AuthService"
      );
    }

    setPendingGoogleRoleSelection(false);

    currentUser.user = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL ?? null,
      ...userDocData,
    };

    Logger.log(
      "info",
      "Google signup completed successfully",
      { userId: user.uid, userType },
      "AuthService"
    );

    return currentUser.user;
  } catch (error) {
    Logger.log(
      "error",
      "Error completing Google signup",
      { error: error.message, userId: user.uid, userType },
      "AuthService"
    );
    throw error;
  }
};

// Login user
const loginUser = async (email, password) => {
  Logger.log("info", "Starting user login", { email }, "AuthService");

  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    Logger.log(
      "info",
      "Firebase Auth login successful",
      { userId: userCredential.user.uid },
      "AuthService"
    );

    // Force a refresh of the currentUser object
    if (userCredential.user) {
      const userDocRef = doc(db, "users", userCredential.user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        Logger.log(
          "debug",
          "Retrieved user data after login",
          { userType: userData.userType, isTeacher: userData.isTeacher },
          "AuthService"
        );

        // Update the currentUser object
        currentUser.user = {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName || userData.name,
          photoURL: userCredential.user.photoURL,
          name: userData.name || userCredential.user.displayName,
          userType: userData.userType || "student",
          isTeacher:
            userData.isTeacher === true || userData.userType === "teacher",
          onboardingStatus:
            userData.onboardingStatus || ONBOARDING_STATUS.PENDING,
          onboardingCompletedAt: userData.onboardingCompletedAt,
          createdAt: userData.createdAt,
        };

        Logger.log(
          "info",
          "Updated current user after login",
          {
            userType: currentUser.user.userType,
            isTeacher: currentUser.user.isTeacher,
          },
          "AuthService"
        );
      }
    }

    Logger.log(
      "info",
      "User login completed successfully",
      { userId: userCredential.user.uid },
      "AuthService"
    );

    return userCredential.user;
  } catch (error) {
    Logger.log(
      "error",
      "User login failed",
      { error: error.message, email },
      "AuthService"
    );
    throw error;
  }
};

// Sign in (or sign up) with Google. Does not touch Firestore — the caller is
// responsible for routing first-time users to the role-selection interstitial
// (see completeGoogleSignup) before any user doc exists.
const signInWithGoogle = async () => {
  Logger.log("info", "Starting Google sign-in", null, "AuthService");

  try {
    const userCredential = await signInWithPopup(auth, googleProvider);
    const isNewUser = !!getAdditionalUserInfo(userCredential)?.isNewUser;

    if (isNewUser) {
      setPendingGoogleRoleSelection(true);
    }

    Logger.log(
      "info",
      "Google sign-in successful",
      { userId: userCredential.user.uid, isNewUser },
      "AuthService"
    );

    return { user: userCredential.user, isNewUser };
  } catch (error) {
    if (
      error.code === "auth/popup-blocked" ||
      error.code === "auth/cancelled-popup-request"
    ) {
      Logger.log(
        "warn",
        "Google sign-in popup blocked, falling back to redirect",
        { error: error.code },
        "AuthService"
      );
      await signInWithRedirect(auth, googleProvider);
      return { redirecting: true };
    }

    Logger.log(
      "error",
      "Google sign-in failed",
      { error: error.code || error.message },
      "AuthService"
    );
    throw error;
  }
};

// Logout user
const logoutUser = async () => {
  Logger.log(
    "info",
    "Starting user logout",
    { userId: currentUser.user?.uid },
    "AuthService"
  );

  try {
    await signOut(auth);
    Logger.log(
      "info",
      "User logout completed successfully",
      null,
      "AuthService"
    );
    return true;
  } catch (error) {
    Logger.log(
      "error",
      "User logout failed",
      { error: error.message },
      "AuthService"
    );
    throw error;
  }
};

// Check if user is authenticated
const isAuthenticated = () => !!currentUser.user;

// Get current user
const getCurrentUser = () => currentUser.user;

// Check if user is a teacher
const isTeacher = () =>
  currentUser.user?.isTeacher === true ||
  currentUser.user?.userType === "teacher";

// Update user profile
const updateUserProfile = async (
  userId,
  profileData = {},
  photoFile = null
) => {
  Logger.log(
    "info",
    "Starting user profile update",
    { userId, hasPhotoFile: !!photoFile },
    "AuthService"
  );

  try {
    // Update the user document in Firestore
    const userDocRef = doc(db, "users", userId);
    await updateDoc(userDocRef, {
      ...profileData,
      updatedAt: new Date(),
    });

    Logger.log(
      "info",
      "Updated user document in Firestore",
      { userId },
      "AuthService"
    );

    // Update teacher profile if the user is a teacher
    if (currentUser.user?.isTeacher) {
      const teacherDocRef = doc(db, "teachers", userId);
      const teacherDoc = await getDoc(teacherDocRef);

      if (teacherDoc.exists()) {
        await updateDoc(teacherDocRef, {
          ...profileData,
          updatedAt: new Date(),
        });

        Logger.log(
          "info",
          "Updated teacher profile document",
          { userId },
          "AuthService"
        );
      }
    }

    // If we have a new photo, upload and update it
    if (photoFile && FeatureFlags.core.photoUpload) {
      // Photo upload logic would go here
      // This would typically use Firebase Storage
      Logger.log(
        "info",
        "Photo upload requested but not implemented",
        { userId },
        "AuthService"
      );
    }

    // Refresh user data after update
    if (auth.currentUser) {
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Update local user state
        currentUser.user = {
          ...currentUser.user,
          ...userData,
        };
      }
    }

    Logger.log(
      "info",
      "User profile update completed successfully",
      { userId },
      "AuthService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error updating user profile",
      { error: error.message, userId },
      "AuthService"
    );
    throw error;
  }
};

// Convert a student to a teacher
const becomeTeacher = async (userId) => {
  Logger.log(
    "info",
    "Converting student to teacher",
    { userId },
    "AuthService"
  );

  try {
    // Update user document
    const userDocRef = doc(db, "users", userId);
    await updateDoc(userDocRef, {
      isTeacher: true,
      userType: "teacher",
      updatedAt: new Date(),
    });

    // Get current user data
    const userDoc = await getDoc(userDocRef);
    const userData = userDoc.data();

    // Create teacher profile if it doesn't exist
    const teacherDocRef = doc(db, "teachers", userId);
    const teacherDoc = await getDoc(teacherDocRef);

    if (!teacherDoc.exists()) {
      await setDoc(teacherDocRef, {
        name: userData.name || "",
        email: userData.email,
        instrument: "",
        bio: "",
        hourlyRate: 0,
        rating: 0,
        reviewCount: 0,
        tags: [],
        availability: [],
        createdAt: new Date(),
      });
    }

    // Update local user state
    if (currentUser.user) {
      currentUser.user = {
        ...currentUser.user,
        isTeacher: true,
        userType: "teacher",
      };
    }

    Logger.log(
      "info",
      "Successfully converted student to teacher",
      { userId },
      "AuthService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error converting student to teacher",
      { error: error.message, userId },
      "AuthService"
    );
    throw error;
  }
};

// Update user onboarding status
const updateOnboardingStatus = async (userId, status, completedAt = null) => {
  Logger.log(
    "info",
    "Updating onboarding status",
    { userId, status },
    "AuthService"
  );

  try {
    const userDocRef = doc(db, "users", userId);
    const updateData = {
      onboardingStatus: status,
      updatedAt: new Date(),
    };

    if (completedAt) {
      updateData.onboardingCompletedAt = completedAt;
    }

    await updateDoc(userDocRef, updateData);

    // Update local user state if it's the current user
    if (currentUser.user && currentUser.user.uid === userId) {
      currentUser.user = {
        ...currentUser.user,
        onboardingStatus: status,
        onboardingCompletedAt: completedAt,
      };
    }

    Logger.log(
      "info",
      "Onboarding status updated successfully",
      { userId, status },
      "AuthService"
    );

    return true;
  } catch (error) {
    Logger.log(
      "error",
      "Error updating onboarding status",
      { error: error.message, userId, status },
      "AuthService"
    );
    throw error;
  }
};

// Partially update the current user object without in-place mutation
const setCurrentUserProps = (updates) => {
  if (currentUser.user) {
    currentUser.user = { ...currentUser.user, ...updates };
  }
};

// Check if user needs onboarding
const needsOnboarding = () => {
  if (!currentUser.user) return false;
  if (currentUser.user.isTeacher) return false; // Teachers don't need student onboarding

  return currentUser.user.onboardingStatus === ONBOARDING_STATUS.PENDING;
};

// Reset password
const resetPassword = async (email) => {
  Logger.log("info", "Starting password reset", { email }, "AuthService");

  try {
    if (!FeatureFlags.core.passwordReset) {
      throw new Error("Password reset feature is disabled");
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error("Invalid email format");
    }

    try {
      // Try the custom Cloud Function (Resend) first
      const sendPasswordResetEmail = httpsCallable(
        functions,
        "sendPasswordResetEmail"
      );

      Logger.log(
        "info",
        "Calling Firebase Function to send password reset email",
        { email },
        "AuthService"
      );

      const result = await sendPasswordResetEmail({ email });

      if (result.data.success) {
        Logger.log(
          "info",
          "Password reset email sent successfully via Resend",
          { email, message: result.data.message },
          "AuthService"
        );
        return {
          success: true,
          message: result.data.message,
        };
      } else {
        throw new Error(
          result.data.message || "Failed to send password reset email"
        );
      }
    } catch (functionError) {
      Logger.log(
        "warn",
        "Custom password reset function failed, falling back to Firebase built-in",
        { error: functionError.message, email },
        "AuthService"
      );

      // Fallback to Firebase's built-in password reset
      await firebaseSendPasswordResetEmail(auth, email);

      Logger.log(
        "info",
        "Password reset email sent successfully via Firebase",
        { email },
        "AuthService"
      );

      return {
        success: true,
        message: "Password reset email sent successfully",
      };
    }
  } catch (error) {
    Logger.log(
      "error",
      "Error resetting password",
      { error: error.message, email },
      "AuthService"
    );

    // Provide user-friendly error messages
    let userMessage =
      "An error occurred while sending the password reset email. Please try again.";

    if (error.code === "auth/user-not-found") {
      userMessage = "If this email exists, a password reset link has been sent";
    } else if (error.code === "auth/invalid-email") {
      userMessage = "Please enter a valid email address.";
    } else if (error.code === "functions/invalid-argument") {
      userMessage = "Please enter a valid email address.";
    } else if (error.code === "functions/internal") {
      userMessage = "Service temporarily unavailable. Please try again later.";
    } else if (error.message === "Password reset feature is disabled") {
      userMessage = "Password reset is currently unavailable.";
    } else if (error.message === "Invalid email format") {
      userMessage = "Please enter a valid email address.";
    }

    const enhancedError = new Error(userMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

export {
  initializeAuthListener,
  registerUser,
  loginUser,
  signInWithGoogle,
  completeGoogleSignup,
  logoutUser,
  isAuthenticated,
  getCurrentUser,
  isTeacher,
  updateUserProfile,
  becomeTeacher,
  updateOnboardingStatus,
  setCurrentUserProps,
  needsOnboarding,
  resetPassword,
  currentUser,
};
